// @ts-check
// SHARED product-checkout data hook (task #95).
//
// The four product-checkout sections (ProductCheckoutPage,
// UpgradableWheelBaseCheckoutPage, GT2CockpitCheckoutPage,
// UDFP20UpgradesCheckoutPage) each independently (a) gated a client-side
// full-offers refetch, (b) read offers from the Overmind store, (c) derived the
// product's gallery images, and (d) picked the offer for the page's itemType.
// That "fix one, forget the other three" duplication is exactly where Dimi's
// #187 fix (correct refetch gate) landed in ONE fork while the other three
// stayed broken, and where the "[object Object]"/galleries-capped-at-2
// regressions kept re-appearing. This hook is the single source of truth for
// that shared logic so the bug class becomes structurally impossible.
//
// It is CLIENT-SAFE: it imports NOTHING manifest-backed. The offers in
// `state.shopItems` are ALREADY ResolvedImage-normalized server-side by
// lib/normalizeOffers (normalizeGallery/toDescriptor) before they hydrate — so
// on the client we only ever COALESCE the already-normalized descriptor fields
// via toImageEntry (utils/imageSrc); we never re-run the server-only resolver.
/** @typedef {import('../lib/schemas').ResolvedImage} ResolvedImage */
/** @typedef {import('../lib/schemas').Offer} Offer */
import { useEffect } from 'react'
// RegionOffers (Overmind→server-props migration, offers slice): the render-time
// offers array comes from the server-threaded region price bundle (grid) before the
// full-offers fetch and from the Zustand offers store (FULL) after. The refetch GATE
// below fires the store's `ensureFullOffers` (Phase D1 — formerly the Overmind
// shopItemsRequiredPageRendered action) and reads the store's `full`/`loading`
// flags. See components/RegionOffers for the provider + sourcing rule.
import { useRegionOffers } from '../components/RegionOffers'
import { useRegionCountryCode } from '../components/RegionGeo'
import { useOffersStore } from '../store/offersStore'
// getOfferImages + getItemOffers + resolveProductItem are the shared PURE
// gallery/offer/item helpers. They were extracted to lib/offerImages (task #191)
// so the SERVER-ONLY LCP resolver (lib/lcpImage) can reuse the EXACT same
// item-resolution + paint-order logic — otherwise its hand-rolled copies drifted
// and preloaded a different image than the carousel painted. Imported here (the
// hook uses/returns them) + re-exported below (callers/tests import them here).
import { getOfferImages, getItemOffers, resolveProductItem } from '../lib/offerImages'

// getOfferImages + getItemOffers now live in lib/offerImages (pure, server-safe)
// so the LCP resolver reuses them verbatim. Re-export so existing callers/tests
// that import them from this module keep working unchanged.
export { getOfferImages, getItemOffers, resolveProductItem }

/**
 * @typedef {Object} UseProductOffers
 * @property {boolean} shopItemsFetched       length-derived; true even for grid-projected SSR data
 * @property {boolean} shopItemsFull          authoritative: true only after the FULL offers are loaded
 * @property {any} item                       the resolved main item (null during SSR/loading or if not found)
 * @property {(offer: Array<any>|null|undefined, mainItem?: any) => Array<ResolvedImage|{src:string}>} getOfferImages  gallery deriver (uncapped)
 * @property {(mainItem: any, opts?: Object) => Array<Array<any>>} getItemOffers  offer-list builder for a set
 */

// resolveProductItem now lives in lib/offerImages (pure, server-safe) so the LCP
// resolver picks the SAME priced regional variant the page does — its gallery images
// are what actually paint. Imported + re-exported above.

/**
 * useProductOffers — shared data logic for the product-checkout sections.
 *
 * Encapsulates the ONE piece every fork shares: the client-side full-offers
 * refetch gate. It gates on `state.shopItemsFull` (NOT the length-derived
 * `state.shopItemsFetched`, which is already true from the grid-projected SSR
 * payload and would silently skip the upgrade, leaving galleries capped at 2 —
 * the #187 bug). It also resolves the page's main item for the given itemType
 * (with optional fallback) and hands back the shared gallery/offer helpers so no
 * fork re-implements them.
 *
 * @param {Object} [params]
 * @param {any} [params.itemType]          the itemType to resolve the main item for
 * @param {any} [params.fallbackItemType]  a fallback itemType if the primary is not found
 * @returns {UseProductOffers}
 */
export function useProductOffers({ itemType, fallbackItemType } = {}) {
  // Render-time offers come from the RegionOffers seam (server-threaded grid
  // offers before the full-offers fetch, upgraded FULL store offers after), NOT
  // from a direct `state.shopItems` read. `offers`/`offersFetched` are
  // byte-identical to the old state reads at SSR + first client render (the
  // provider value === the hydrated grid) and to the store thereafter.
  const { offers, offersFetched, offersFull } = useRegionOffers()
  // The pricing country the full-offers fetch prices against — the RegionGeo seam
  // (client-resolved region-store value, else SSR server prop, else Overmind),
  // replacing the former state.newRegion the Overmind getShopItems operator read.
  const region = useRegionCountryCode()
  const ensureFullOffers = useOffersStore((s) => s.ensureFullOffers)

  useEffect(() => {
    // Gate on offersFull (not offersFetched): the SSR payload is the grid-only
    // projection (galleries capped at 2, set images dropped — task #76), so a
    // product page must fetch the FULL offers to show complete galleries and real
    // bundle/set images. offersFetched is length-derived and already true from the
    // grid-projected SSR data, so gating on it skips the upgrade and the carousel
    // stays capped at 2 images (task #187). ensureFullOffers is itself idempotent
    // (store-level #188 dedup + #187 full-vs-grid), so the mount call is safe.
    ensureFullOffers(region)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resolve the main item once the full offers are available. Kept null during
  // SSR/loading so callers render their reserved-box loading state (CLS-safe).
  const item = offersFetched
    ? resolveProductItem(itemType, fallbackItemType, offers)
    : null

  return {
    // The effective offers array (server-threaded grid, full after the fetch) so
    // callers can pass it to the shared shopItems helpers instead of reading
    // state.shopItems directly.
    offers,
    shopItemsFetched: offersFetched,
    shopItemsFull: offersFull,
    item,
    getOfferImages,
    getItemOffers,
  }
}

export default useProductOffers
